import data from '../../data/top-searches/2025-11.json';

export type Category = 'Camera' | 'Lens' | 'Drone' | 'Accessory' | 'Lighting' | 'Video';
export type Condition = 'L' | 'G' | 'F'; // Like New, Good, Fair
export type DemandLevel = 'Low' | 'Medium' | 'High';

export type TopSearchItem = {
  rank: number;
  category: Category;
  brand: string;
  model: string;
  low_price: number;
  high_price: number;
  note_buyer?: string;
  rank_delta?: number;
};

export type TopSearchItemWithDerived = TopSearchItem & {
  median_price: number;
  demand_level: DemandLevel;
  note_seller: string;
  sku_id: string;
};

/**
 * Load top 50 items from JSON snapshot
 */
export async function loadTop50Nov2025(): Promise<TopSearchItemWithDerived[]> {
  try {
    // Validate and filter invalid rows
    const validItems = data.filter(item => {
      return (
        item.brand &&
        item.model &&
        item.low_price < item.high_price
      );
    });

    // Add derived fields
    return validItems.map(item => ({
      ...item,
      median_price: calculateMedian(item.low_price, item.high_price),
      demand_level: getDemandLevel(item.rank),
      note_seller: generateSellerNote(item.category as Category, item.note_buyer),
      sku_id: `${item.brand}:${item.model}`,
    } as TopSearchItemWithDerived));
  } catch (error) {
    console.error('Failed to load top searches data:', error);
    return [];
  }
}

/**
 * Calculate median price rounded to nearest $5
 */
export function calculateMedian(low: number, high: number): number {
  return Math.round(((low + high) / 2) / 5) * 5;
}

/**
 * Calculate suggested ask based on condition
 */
export function suggestedAsk(
  low: number,
  high: number,
  condition: Condition
): number {
  const median = calculateMedian(low, high);

  if (condition === 'L') {
    // Like New: +10% over median, capped at high
    return Math.min(high, Math.round((median * 1.10) / 5) * 5);
  }

  if (condition === 'F') {
    // Fair: -12% under median, floored at low
    return Math.max(low, Math.round((median * 0.88) / 5) * 5);
  }

  // Good: median
  return median;
}

/**
 * Determine demand level based on rank
 */
export function getDemandLevel(rank: number): DemandLevel {
  if (rank <= 10) return 'High';
  if (rank <= 30) return 'Medium';
  return 'Low';
}

/**
 * Generate seller-focused notes based on category
 */
export function generateSellerNote(
  category: Category,
  buyerNote?: string
): string {
  // Category-specific templates
  const templates: Record<Category, string> = {
    Camera:
      'Add shutter count and a clean sensor photo. Include battery/charger to reach upper range.',
    Lens:
      'Shoot front/rear elements, wide open and stopped down. Call out dust or zoom creep to prevent returns.',
    Drone:
      'State flight hours and firmware. Props, ND filters, and case sell faster as a kit.',
    Accessory:
      'Confirm cables/adapters. Bundle with related items as an add-on, not baked into price.',
    Lighting:
      'Show powered-on photo. Note output/CRI and any modifiers included.',
    Video:
      'State recording hours and firmware version. Include batteries, media, and cage/rig components. Note sensor condition and codec compatibility.',
  };

  // Start with category template
  let sellerNote = templates[category];

  // If buyer note exists, append converted version
  if (buyerNote && buyerNote.trim()) {
    // Convert buyer-oriented language to seller imperatives
    const converted = buyerNote
      .replace(/look for/gi, 'show')
      .replace(/check for/gi, 'note')
      .replace(/buyers want/gi, 'include')
      .replace(/avoid/gi, 'disclose');

    sellerNote += ` Market note: ${converted}`;
  }

  return sellerNote;
}

/**
 * Format price for display
 * If difference is less than $50, show single value
 */
export function formatPriceRange(low: number, high: number): string {
  const diff = high - low;

  if (diff < 50) {
    return `$${low.toLocaleString()}`;
  }

  return `$${low.toLocaleString()} – $${high.toLocaleString()}`;
}

/**
 * Get all unique brands from data
 */
export function getAllBrands(items: TopSearchItemWithDerived[]): string[] {
  const brands = new Set(items.map(item => item.brand));
  return Array.from(brands).sort();
}

/**
 * Get all available categories
 */
export function getAllCategories(): Category[] {
  return ['Camera', 'Lens', 'Drone', 'Accessory', 'Lighting', 'Video'];
}
